import React, { useState, useEffect, useMemo } from "react";
import { useCostData, useTeamData, useAppStore, CURRENCY_RATES } from "../stores/appStore";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "./ui/chart";
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, PieChart, Pie, Cell, BarChart, Bar, LineChart, Line } from "recharts";
import { Download, Calculator, Users, DollarSign, TrendingUp, FileText, ChevronDown, ChevronRight } from "lucide-react";
import { Header } from "./header";
import { CostBreakdownPDF } from "./CostBreakdownPDF";
import OperatingCostsManager from "./OperatingCostsManager";
import { pdf } from "@react-pdf/renderer";

interface TeamRole {
  id: string;
  name: string;
  salary: number;
  hourlyRate?: number; // optional hourly rate in USD, used to auto-calculate salary
  headcount: number[]; // headcount for each month
  category: 'founder' | 'engineering' | 'design' | 'operations' | 'sales' | 'support';
}

interface OperatingCost {
  id: string;
  name: string;
  monthlyCosts: number[]; // cost for each month
  category: 'infrastructure' | 'facilities' | 'tools' | 'marketing' | 'other';
}



interface CostBreakdownProps {
  showHeader?: boolean;
  revenueData?: Array<{
    month: number;
    revenue: number;
    expenses: number;
    operatingExpenses: number;
    baseExpenses: number;
  }>;
}

interface CostBreakdownData {
  teamRoles: TeamRole[];
  operatingCosts: OperatingCost[];
  projectionMonths: number;
}



// Base salaries before multiplier (market rates)
const baseTeamRoles: TeamRole[] = [
  { id: 'founder', name: 'Founder/CEO', salary: 140000, headcount: Array(22).fill(1), category: 'founder' },
  { id: 'senior-swe', name: 'Senior Software Engineer', salary: 120000, hourlyRate: 58, headcount: [1,1,1,1,2,2,2,2,2,2,2,2,2,2,2,2,2,2], category: 'engineering' },
  { id: 'intermediate-swe', name: 'Software Engineer', salary: 90000, hourlyRate: 43, headcount: Array(22).fill(2), category: 'engineering' },
  { id: 'junior-swe', name: 'Junior Software Engineer', salary: 60000, hourlyRate: 29, headcount: [2,2,2,2,2,2,2,2,2,5,5,5,5,5,5,5,5,5], category: 'engineering' },
  { id: 'senior-designer', name: 'Senior Product Designer', salary: 100000, headcount: Array(22).fill(0), category: 'design' },
  { id: 'junior-designer', name: 'Product Designer', salary: 70000, headcount: Array(22).fill(2), category: 'design' },
  { id: 'devops', name: 'DevOps Engineer', salary: 110000, headcount: Array(22).fill(1), category: 'engineering' },
  { id: 'operations', name: 'Operations Manager', salary: 80000, headcount: Array(22).fill(1), category: 'operations' },
  { id: 'sales-marketing', name: 'Sales & Marketing', salary: 75000, headcount: [0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1], category: 'sales' },
  { id: 'accountant', name: 'Accountant', salary: 65000, headcount: [0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1], category: 'operations' },
  { id: 'customer-success', name: 'Customer Success', salary: 55000, headcount: [1,1,1,1,2,2,2,2,2,2,2,2,2,2,2,2,2,2], category: 'support' },
];

// Function to calculate annual salary from hourly rate (40 hours/week * 52 weeks = 2080 hours/year)
const calculateSalaryFromHourlyRate = (hourlyRate: number): number => {
  return Math.round(hourlyRate * 2080);
};

// Function to get team roles with salary multiplier applied
const getTeamRolesWithMultiplier = (multiplier: number): TeamRole[] => {
  return baseTeamRoles.map(role => {
    // If hourlyRate is provided, use it to calculate salary, otherwise use existing salary
    const baseSalary = role.hourlyRate ? calculateSalaryFromHourlyRate(role.hourlyRate) : role.salary;
    return {
      ...role,
      salary: Math.round(baseSalary * multiplier)
    };
  });
};

const defaultOperatingCosts: OperatingCost[] = [
  { id: 'cloud', name: 'Cloud Infrastructure (AWS/GCP)', monthlyCosts: [10000,10000,10000,15000,15000,15000,25000,25000,25000,40000,40000,40000,40000,40000,40000,40000,40000,40000], category: 'infrastructure' },
  { id: 'rent', name: 'Office Rent', monthlyCosts: [18000,18000,18000,30000,30000,30000,30000,30000,60000,60000,60000,60000,60000,60000,60000,60000,60000,60000,60000,60000,60000,60000], category: 'facilities' },
  { id: 'utilities', name: 'Utilities & Internet', monthlyCosts: Array(22).fill(15000), category: 'facilities' },
  { id: 'tools', name: 'Development & Productivity Tools', monthlyCosts: Array(22).fill(11600), category: 'tools' },
  { id: 'crm', name: 'CRM & Sales Tools', monthlyCosts: Array(22).fill(4100), category: 'tools' },
  { id: 'accounting', name: 'Accounting & Legal', monthlyCosts: Array(22).fill(8000), category: 'other' },
  { id: 'marketing', name: 'Marketing & Advertising', monthlyCosts: [30000,30000,30000,30000,30000,30000,30000,50000,50000,50000,50000,50000,50000,50000,50000,50000,50000,50000], category: 'marketing' },
];

export default function CostBreakdown({ showHeader = true, revenueData }: CostBreakdownProps) {
  const state = useAppStore();
  const { teamRoles, updateTeamRole, updateHeadcount } = useTeamData();
  const { currency } = state;

  const formatCurrency = (value: number) => {
    // Convert value to selected currency
    const convertedValue = value * CURRENCY_RATES[currency];

    // Currency formatting configuration
    const currencyConfig = {
      USD: { locale: "en-US", currency: "USD" },
      AED: { locale: "ar-AE", currency: "AED" },
      INR: { locale: "en-IN", currency: "INR" },
    };

    const config = currencyConfig[currency];

    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: currency === 'USD' ? 0 : 0,
      maximumFractionDigits: currency === 'USD' ? 0 : 0,
    }).format(convertedValue);
  };

  const getCurrencySymbol = () => {
    const currencySymbols = {
      USD: '$',
      AED: 'د.إ',
      INR: '₹',
    };
    return currencySymbols[currency];
  };
  const { operatingCosts, setOperatingCosts, companyProfile, setCompanyProfile } = useCostData();
  const {
    projectName,
    description,
    valuationType,
    investmentData,
    shareholders,
    expenses,
    projectionMonths,
    costBreakdownEnabled,
    selectedTemplate,
  } = state;
  const [activeTab, setActiveTab] = useState('setup');
  const [selectedMonth, setSelectedMonth] = useState(5); // Default to month 6
  const [teamSectionCollapsed, setTeamSectionCollapsed] = useState(false);
  const [operatingSectionCollapsed, setOperatingSectionCollapsed] = useState(false);
  // Use data directly from Zustand store
  const costBreakdownData = {
    teamRoles,
    operatingCosts,
    projectionMonths,
    companyProfile,
  };

  const costData = {
    teamRoles,
    operatingCosts,
    projectionMonths,
    activeScenarioId: null,
  };

  // Stub function for backward compatibility
  const setCostData = (updater: any) => {
    // This is a stub - the actual state is managed by Zustand
    console.log('setCostData called with:', updater);
  };

  // Sync with store on mount and when monthlyOpex changes
  useEffect(() => {
    if (investmentData.monthlyOpex > 0 && !costBreakdownEnabled) {
      // Initialize operating costs with monthlyOpex from main calculator
      const updatedOperatingCosts = operatingCosts.map(cost => {
        if (cost.id === 'cloud') {
          return { ...cost, monthlyCosts: cost.monthlyCosts.map(() => Math.round(investmentData.monthlyOpex * 0.1)) };
        }
        if (cost.id === 'rent') {
          return { ...cost, monthlyCosts: cost.monthlyCosts.map(() => Math.round(investmentData.monthlyOpex * 0.12)) };
        }
        if (cost.id === 'utilities') {
          return { ...cost, monthlyCosts: cost.monthlyCosts.map(() => Math.round(investmentData.monthlyOpex * 0.06)) };
        }
        if (cost.id === 'tools') {
          return { ...cost, monthlyCosts: cost.monthlyCosts.map(() => Math.round(investmentData.monthlyOpex * 0.0464)) };
        }
        if (cost.id === 'crm') {
          return { ...cost, monthlyCosts: cost.monthlyCosts.map(() => Math.round(investmentData.monthlyOpex * 0.0164)) };
        }
        if (cost.id === 'accounting') {
          return { ...cost, monthlyCosts: cost.monthlyCosts.map(() => Math.round(investmentData.monthlyOpex * 0.032)) };
        }
        if (cost.id === 'marketing') {
          return { ...cost, monthlyCosts: cost.monthlyCosts.map(() => Math.round(investmentData.monthlyOpex * 0.12)) };
        }
        return cost;
      });

      // Apply salary multiplier to team roles based on company profile
      const updatedTeamRoles = getTeamRolesWithMultiplier(companyProfile.salaryMultiplier);

      // Team roles are managed separately in the store
      state.setCostBreakdownEnabled(true);
    }
  }, [investmentData.monthlyOpex, costBreakdownEnabled, operatingCosts, companyProfile.salaryMultiplier]);





  const updateOperatingCost = (id: string, monthIndex: number, cost: number) => {
    // Update operating costs in the store
    const updatedCosts = operatingCosts.map(opCost =>
      opCost.id === id
        ? {
            ...opCost,
            monthlyCosts: opCost.monthlyCosts.map((monthlyCost, index) =>
              index === monthIndex ? cost : monthlyCost
            )
          }
        : opCost
    );
    setOperatingCosts(updatedCosts);
  };

  const calculateMonthlySalaryCost = (monthIndex: number): number => {
    return costData.teamRoles.reduce((total, role) => {
      const headcount = role.headcount[monthIndex] || 0;
      return total + (headcount * role.salary);
    }, 0);
  };

  const calculateMonthlyOperatingCost = (monthIndex: number): number => {
    return costData.operatingCosts.reduce((total, opCost) => {
      return total + (opCost.monthlyCosts[monthIndex] || 0);
    }, 0);
  };

  const calculateTotalMonthlyCost = (monthIndex: number): number => {
    const salaryCost = calculateMonthlySalaryCost(monthIndex);
    const operatingCost = calculateMonthlyOperatingCost(monthIndex);
    return salaryCost + operatingCost;
  };

  const calculateTotalHeadcount = (monthIndex: number): number => {
    return costData.teamRoles.reduce((total, role) => {
      return total + (role.headcount[monthIndex] || 0);
    }, 0);
  };

  const calculateTotalSalaryCosts = useMemo((): number => {
    return Array.from({ length: costData.projectionMonths }, (_, i) => calculateMonthlySalaryCost(i))
      .reduce((sum, cost) => sum + cost, 0);
  }, [costData.teamRoles, costData.projectionMonths]);

  const calculateTotalOperatingCosts = useMemo((): number => {
    return Array.from({ length: costData.projectionMonths }, (_, i) => calculateMonthlyOperatingCost(i))
      .reduce((sum, cost) => sum + cost, 0);
  }, [costData.operatingCosts, costData.projectionMonths]);

  // Memoize frequently used arrays to prevent recreation on every render
  const monthIndices = useMemo(() =>
    Array.from({ length: costData.projectionMonths }, (_, i) => i),
    [costData.projectionMonths]
  );

  const displayMonthIndices = useMemo(() =>
    Array.from({ length: Math.min(costData.projectionMonths, 12) }, (_, i) => i),
    [costData.projectionMonths]
  );

  // Heatmap color functions
  const getHeadcountColor = (value: number): string => {
    if (value === 0) return 'bg-muted/40 text-muted-foreground';
    if (value === 1) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    if (value === 2) return 'bg-blue-200 text-blue-900 dark:bg-blue-800/40 dark:text-blue-200';
    if (value === 3) return 'bg-blue-300 text-blue-900 dark:bg-blue-700/50 dark:text-blue-100';
    if (value === 4) return 'bg-blue-400 text-white dark:bg-blue-600/60 dark:text-white';
    return 'bg-blue-500 text-white dark:bg-blue-500/70 dark:text-white';
  };

  const getCostColor = (value: number, maxValue: number): string => {
    if (value === 0) return 'bg-muted/40 text-muted-foreground';

    const intensity = Math.min(value / maxValue, 1);
    if (intensity < 0.2) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    if (intensity < 0.4) return 'bg-green-200 text-green-900 dark:bg-green-800/40 dark:text-green-200';
    if (intensity < 0.6) return 'bg-yellow-200 text-yellow-900 dark:bg-yellow-800/40 dark:text-yellow-200';
    if (intensity < 0.8) return 'bg-orange-200 text-orange-900 dark:bg-orange-700/50 dark:text-orange-200';
    return 'bg-red-200 text-red-900 dark:bg-red-600/60 dark:text-red-100';
  };

  // Calculate max values for cost heatmap normalization
  const getMaxSalary = (): number => {
    return Math.max(...costData.teamRoles.map(role => role.salary), 1);
  };

  const getMaxOperatingCost = (): number => {
    return Math.max(...costData.operatingCosts.flatMap(cost => cost.monthlyCosts), 1);
  };

  const getCostBreakdownData = (monthIndex: number) => {
    const salaryCost = calculateMonthlySalaryCost(monthIndex);
    const operatingCosts = costData.operatingCosts.map(opCost => ({
      name: opCost.name,
      value: opCost.monthlyCosts[monthIndex] || 0,
      category: opCost.category,
    })).filter(item => item.value > 0);

    return [
      { name: 'Salaries', value: salaryCost, category: 'salaries' },
      ...operatingCosts,
    ];
  };

  const getCategoryBreakdownData = (monthIndex: number) => {
    const data = getCostBreakdownData(monthIndex);
    const categories = data.reduce((acc, item) => {
      const category = item.category;
      if (!acc[category]) {
        acc[category] = 0;
      }
      acc[category] += item.value;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(categories).map(([category, value]) => ({
      category: category.charAt(0).toUpperCase() + category.slice(1),
      value,
    }));
  };

  const exportToCSV = () => {
    const headers = ['Month', 'Total Headcount', 'Salary Costs', ...costData.operatingCosts.map(c => c.name), 'Total Operating Cost', 'Grand Total'];
    const rows = Array.from({ length: costData.projectionMonths }, (_, monthIndex) => {
      const month = monthIndex + 1;
      const headcount = calculateTotalHeadcount(monthIndex);
      const salaryCost = calculateMonthlySalaryCost(monthIndex);
      const operatingCosts = costData.operatingCosts.map(opCost => opCost.monthlyCosts[monthIndex] || 0);
      const totalOperatingCost = calculateMonthlyOperatingCost(monthIndex);
      const grandTotal = calculateTotalMonthlyCost(monthIndex);

      return [month, headcount, salaryCost, ...operatingCosts, totalOperatingCost, grandTotal];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cost-breakdown.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const syncToMainCalculator = () => {
    // Calculate average monthly cost for the first 6 months as the new monthlyOpex
    const averageMonthlyCost = Array.from({ length: 6 }, (_, i) => calculateTotalMonthlyCost(i))
      .reduce((sum, cost) => sum + cost, 0) / 6;

    const totalHeadcount = calculateTotalHeadcount(21); // Month 22 headcount

    updateData({
      monthlyOpex: Math.round(averageMonthlyCost),
      totalHeadcount,
      totalMonthlyCost: Math.round(averageMonthlyCost),
    });

    // Show success message (you might want to add toast here)
    alert(`Synced to main calculator:\n• Monthly OPEX: ₹${Math.round(averageMonthlyCost).toLocaleString()}\n• Total Headcount: ${totalHeadcount}`);
  };

  const exportToPDF = async () => {
    const doc = (
      <CostBreakdownPDF
        projectName={state.projectName}
        teamRoles={costData.teamRoles}
        operatingCosts={costData.operatingCosts}
        projectionMonths={costData.projectionMonths}
        calculateTotalHeadcount={calculateTotalHeadcount}
        calculateMonthlySalaryCost={calculateMonthlySalaryCost}
        calculateMonthlyOperatingCost={calculateMonthlyOperatingCost}
        calculateTotalMonthlyCost={calculateTotalMonthlyCost}
        formatCurrency={formatCurrency}
      />
    );

    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.projectName}-cost-breakdown.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };



  return (
    <div className={showHeader ? "min-h-screen bg-background" : ""}>
      {showHeader && (
        <>
          <Header
            showHero={false}
            title="Cost Projection Tool"
            description="22-month financial planning for SaaS startups"
          />

          {/* Action Buttons */}
          <div className="border-b bg-background/50">
            <div className="container mx-auto px-6 py-4">
              <div className="flex justify-end gap-2">
                <Button onClick={syncToMainCalculator} variant="default" size="sm">
                  <Calculator className="w-4 h-4 mr-2" />
                  Sync to Calculator
                </Button>
                <Button onClick={exportToPDF} variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
                <Button onClick={exportToCSV} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="container mx-auto px-6 py-8">




        {/* Operating Costs Manager */}
        <OperatingCostsManager planningMonths={costData.projectionMonths} />

        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Month 6 Cost</p>
                   <p className="text-lg font-bold">₹{(calculateTotalMonthlyCost(5) / 1000).toFixed(0)}K</p>
                </div>
                <DollarSign className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Month 12 Cost</p>
                   <p className="text-lg font-bold">₹{(calculateTotalMonthlyCost(11) / 1000).toFixed(0)}K</p>
                </div>
                <TrendingUp className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Headcount</p>
                   <p className="text-lg font-bold">{calculateTotalHeadcount(17)}</p>
                </div>
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">22-Month Total</p>
                   <p className="text-lg font-bold">₹{(Array.from({ length: 22 }, (_, i) => calculateTotalMonthlyCost(i)).reduce((a, b) => a + b, 0) / 100000).toFixed(0)}L</p>
                </div>
                <Calculator className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Headcount Growth Chart */}
        <Card className="mb-8 border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-muted-foreground" />
              Headcount Growth Projection
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              Team expansion over 22 months based on your hiring plan
            </CardDescription>
          </CardHeader>
           <CardContent className="p-6">
             <ChartContainer
               config={{
                 headcount: {
                   label: "Total Headcount",
                   color: "hsl(221.2 83.2% 53.3%)",
                 },
               }}
               className="w-full h-[350px]"
             >
               <AreaChart
                 data={Array.from({ length: costData.projectionMonths }, (_, monthIndex) => ({
                   month: monthIndex + 1,
                   headcount: calculateTotalHeadcount(monthIndex),
                 }))}
                 margin={{
                   left: 20,
                   right: 30,
                   top: 20,
                   bottom: 20,
                 }}
               >
                 <defs>
                   <linearGradient id="headcountGradient" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="5%" stopColor="hsl(221.2 83.2% 53.3%)" stopOpacity={0.3}/>
                     <stop offset="50%" stopColor="hsl(221.2 83.2% 53.3%)" stopOpacity={0.1}/>
                     <stop offset="95%" stopColor="hsl(221.2 83.2% 53.3%)" stopOpacity={0.02}/>
                   </linearGradient>
                 </defs>
                 <CartesianGrid
                   vertical={false}
                   strokeDasharray="2 2"
                   stroke="hsl(var(--border))"
                   opacity={0.3}
                 />
                 <XAxis
                   dataKey="month"
                   axisLine={false}
                   tickLine={false}
                   tickMargin={8}
                   tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                   tickFormatter={(value) => `M${value}`}
                 />
                 <YAxis
                   axisLine={false}
                   tickLine={false}
                   tickMargin={8}
                   tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                   tickFormatter={(value) => value}
                 />
                 <ChartTooltip
                   cursor={false}
                   contentStyle={{
                     backgroundColor: 'hsl(var(--background))',
                     border: '1px solid hsl(var(--border))',
                     borderRadius: '8px',
                     boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                     fontSize: '14px'
                   }}
                   content={
                     <ChartTooltipContent
                       formatter={(value) => [value, "Headcount"]}
                       labelFormatter={(label) => `Month ${label}`}
                     />
                   }
                 />
                 <Area
                   type="monotone"
                   dataKey="headcount"
                   stroke="hsl(221.2 83.2% 53.3%)"
                   strokeWidth={2}
                   fillOpacity={1}
                   fill="url(#headcountGradient)"
                 />
               </AreaChart>
             </ChartContainer>
           </CardContent>
        </Card>

        {/* Cost Breakdown Chart */}
        <Card className="mb-8 border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="w-5 h-5 text-muted-foreground" />
              Cost Breakdown by Category
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              Breakdown of monthly costs by category for the selected month
            </CardDescription>
            <div className="flex items-center gap-2 mt-2">
              <Label htmlFor="month-select" className="text-sm">Month:</Label>
              <Select
                value={selectedMonth.toString()}
                onValueChange={(value) => setSelectedMonth(Number(value))}
              >
                <SelectTrigger className="w-32 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: costData.projectionMonths }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      Month {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-6">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               {/* Pie Chart */}
               <div>
                 <ChartContainer
                   config={{
                     salaries: { label: "Salaries", color: "hsl(221.2 83.2% 53.3%)" },
                     infrastructure: { label: "Infrastructure", color: "hsl(142 71% 45%)" },
                     facilities: { label: "Facilities", color: "hsl(38 92% 50%)" },
                     tools: { label: "Tools", color: "hsl(0 84.2% 60.2%)" },
                     marketing: { label: "Marketing", color: "hsl(262 83% 58%)" },
                     other: { label: "Other", color: "hsl(210 40% 60%)" },
                   }}
                   className="w-full h-[350px]"
                 >
                   <PieChart>
                     <Pie
                       data={getCategoryBreakdownData(selectedMonth)}
                       cx="50%"
                       cy="50%"
                       outerRadius={110}
                       innerRadius={45}
                       dataKey="value"
                       label={({ category, value }) => `${category}: ₹${(value / 1000).toFixed(0)}K`}
                       labelLine={false}
                       style={{ fontSize: '12px', fontWeight: '500' }}
                     >
                       {getCategoryBreakdownData(selectedMonth).map((entry, index) => {
                         const colors = [
                           'hsl(221.2 83.2% 53.3%)', // chart-1
                           'hsl(142 71% 45%)',       // chart-2
                           'hsl(38 92% 50%)',        // chart-3
                           'hsl(0 84.2% 60.2%)',     // chart-4
                           'hsl(262 83% 58%)',       // chart-5
                           'hsl(210 40% 60%)'        // chart-6
                         ];
                         return (
                           <Cell
                             key={`cell-${index}`}
                             fill={colors[index % 6]}
                           />
                         );
                       })}
                     </Pie>
                     <ChartTooltip
                       contentStyle={{
                         backgroundColor: 'hsl(var(--background))',
                         border: '1px solid hsl(var(--border))',
                         borderRadius: '8px',
                         boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                         fontSize: '14px'
                       }}
                       content={
                         <ChartTooltipContent
                           formatter={(value) => [`₹${typeof value === 'number' ? value.toLocaleString() : value}`]}
                         />
                       }
                     />
                   </PieChart>
                 </ChartContainer>
               </div>

               {/* Bar Chart */}
               <div>
                 <ChartContainer
                   config={{
                     value: { label: "Amount", color: "hsl(221.2 83.2% 53.3%)" },
                   }}
                   className="w-full h-[350px]"
                 >
                   <BarChart
                     data={getCategoryBreakdownData(selectedMonth)}
                     margin={{ left: 20, right: 30, top: 20, bottom: 20 }}
                   >
                     <CartesianGrid vertical={false} strokeDasharray="2 2" stroke="hsl(var(--border))" opacity={0.3} />
                     <XAxis
                       dataKey="category"
                       axisLine={false}
                       tickLine={false}
                       tickMargin={8}
                       tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                       angle={-45}
                       textAnchor="end"
                       height={80}
                     />
                     <YAxis
                       axisLine={false}
                       tickLine={false}
                       tickMargin={8}
                       tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                       tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                     />
                     <ChartTooltip
                       contentStyle={{
                         backgroundColor: 'hsl(var(--background))',
                         border: '1px solid hsl(var(--border))',
                         borderRadius: '8px',
                         boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                         fontSize: '14px'
                       }}
                       content={
                         <ChartTooltipContent
                           formatter={(value) => [`₹${typeof value === 'number' ? value.toLocaleString() : value}`]}
                         />
                       }
                     />
                     <Bar
                       dataKey="value"
                       fill="hsl(221.2 83.2% 53.3%)"
                       radius={[4, 4, 0, 0]}
                     />
                   </BarChart>
                 </ChartContainer>
               </div>
             </div>
          </CardContent>
         </Card>

        {/* Monthly Revenue vs Expenses Chart */}
        {revenueData && revenueData.length > 0 && (
          <Card className="mb-8 border-0 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-muted-foreground" />
                Monthly Revenue vs Expenses
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Revenue and total expense comparison over time (includes salaries, operating costs, and base OPEX)
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <ChartContainer
                config={{
                  revenue: {
                    label: "Monthly Revenue",
                    color: "hsl(var(--chart-2))",
                  },
                  expenses: {
                    label: "Total Expenses",
                    color: "hsl(var(--chart-1))",
                  },
                  operatingExpenses: {
                    label: "Operating Costs",
                    color: "hsl(var(--chart-4))",
                  },
                  baseExpenses: {
                    label: "Base OPEX",
                    color: "hsl(var(--chart-3))",
                  },
                }}
                className="w-full h-[350px]"
              >
                <LineChart
                  data={revenueData.slice(0, costData.projectionMonths)}
                  margin={{
                    left: 12,
                    right: 12,
                    top: 12,
                    bottom: 12,
                  }}
                >
                  <CartesianGrid
                    vertical={false}
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tickMargin={8}
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `M${value + 1}`}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickMargin={8}
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                  />
                  <ChartTooltip
                    cursor={false}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px'
                    }}
                    content={
                      <ChartTooltipContent
                        formatter={(value) => [`₹${typeof value === 'number' ? value.toLocaleString() : value}`]}
                        labelFormatter={(label) => `Month ${label + 1}`}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={3}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="expenses"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={3}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="operatingExpenses"
                    stroke="hsl(var(--chart-4))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="baseExpenses"
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={2}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                </LineChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}

        {/* Detailed Tables */}
        <div className="space-y-8">
          {/* Team Roles Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTeamSectionCollapsed(!teamSectionCollapsed)}
                    className="p-1 h-6 w-6"
                  >
                    {teamSectionCollapsed ? (
                      <ChevronRight className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                  <CardTitle className="text-lg">
                    <Users className="w-5 h-5 text-muted-foreground inline mr-2" />
                    Team Headcount & Salaries
                  </CardTitle>
                </div>
                {teamSectionCollapsed && (
                  <div className="text-right">
                     <div className="text-sm text-muted-foreground">Total 22-Month Cost</div>
                     <div className="text-lg font-semibold">₹{(calculateTotalSalaryCosts / 100000).toFixed(0)}L</div>
                  </div>
                )}
              </div>
              {!teamSectionCollapsed && (
                 <CardDescription className="text-sm text-muted-foreground ml-8">
                   Configure your team composition and salary expectations over 22 months
                 </CardDescription>
              )}
            </CardHeader>
            {!teamSectionCollapsed && (
              <CardContent className="p-0">
                <div className="overflow-x-auto border border-border rounded-lg">
                  <Table className="border-collapse">
                     <TableHeader>
                       <TableRow className="border-b-2 border-border bg-muted/30 hover:bg-muted/30">
                         <TableHead className="text-left py-3 px-4 text-sm font-semibold text-foreground border-r border-border min-w-[200px]">Role</TableHead>
                         <TableHead className="text-left py-3 px-4 text-sm font-semibold text-foreground border-r border-border min-w-[120px]">Hourly Rate</TableHead>
                         <TableHead className="text-left py-3 px-4 text-sm font-semibold text-foreground border-r border-border min-w-[140px]">Annual Salary</TableHead>
                         <TableHead className="text-left py-3 px-4 text-sm font-semibold text-foreground border-r border-border min-w-[120px]">Category</TableHead>
                         {displayMonthIndices.map((i) => (
                           <TableHead key={i} className="text-center py-3 px-2 text-sm font-semibold text-foreground border-r border-border last:border-r-0 min-w-[60px]">
                             M{i + 1}
                           </TableHead>
                         ))}
                          {costData.projectionMonths > 12 && (
                            <TableHead className="text-center py-3 px-2 text-sm font-semibold text-foreground min-w-[60px]">
                              M22
                            </TableHead>
                          )}
                       </TableRow>
                     </TableHeader>
                     <TableBody>
                       {costData.teamRoles.map((role, index) => (
                         <TableRow key={role.id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                           <TableCell className="py-2 px-4 border-r border-border/30">
                             <input
                               value={role.name}
                               onChange={(e) => updateTeamRole(role.id, { name: e.target.value })}
                               className="bg-transparent border-none outline-none font-medium text-sm w-full h-8 px-2 rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
                               placeholder="Role name"
                             />
                           </TableCell>
                           <TableCell className="py-2 px-4 border-r border-border/30">
                             <div className="relative">
                               <input
                                 type="number"
                                 value={role.hourlyRate || ''}
                                 onChange={(e) => {
                                   const hourlyRate = e.target.value ? Number(e.target.value) : undefined;
                                   const updates: Partial<TeamRole> = { hourlyRate };
                                   if (hourlyRate) {
                                     // Auto-calculate salary from hourly rate
                                     updates.salary = calculateSalaryFromHourlyRate(hourlyRate);
                                   }
                                   updateTeamRole(role.id, updates);
                                 }}
                                 className="w-full h-8 pr-8 text-sm bg-transparent border-none outline-none rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all text-right"
                                 min="0"
                                 step="0.01"
                                 placeholder="Optional"
                               />
                               {role.hourlyRate && (
                                 <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm z-10">{getCurrencySymbol()}/hr</span>
                               )}
                             </div>
                           </TableCell>
                           <TableCell className={`py-2 px-4 border-r border-border/30 ${getCostColor(role.salary, getMaxSalary())}`}>
                             <div className="relative">
                               <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm z-10">₹</span>
                               <input
                                 type="number"
                                 value={role.salary}
                                 onChange={(e) => updateTeamRole(role.id, { salary: Number(e.target.value), hourlyRate: undefined })}
                                 className="w-full h-8 pl-6 pr-2 text-sm bg-transparent border-none outline-none rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all text-right"
                                 min="0"
                               />
                             </div>
                           </TableCell>
                           <TableCell className="py-2 px-4 border-r border-border/30">
                             <Badge variant="outline" className="capitalize text-xs h-6">
                               {role.category}
                             </Badge>
                           </TableCell>
                          {role.headcount.slice(0, 12).map((count, monthIndex) => (
                            <TableCell key={monthIndex} className={`py-2 px-2 text-center border-r border-border/30 last:border-r-0 ${getHeadcountColor(count)}`}>
                              <input
                                type="number"
                                value={count}
                                onChange={(e) => updateHeadcount(role.id, monthIndex, Number(e.target.value))}
                                className="w-full h-8 text-sm text-center bg-transparent border-none outline-none rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
                                min="0"
                              />
                            </TableCell>
                          ))}
                          {costData.projectionMonths > 12 && (
                            <TableCell className={`py-2 px-2 text-center ${getHeadcountColor(role.headcount[17])}`}>
                              <input
                                type="number"
                                value={role.headcount[17]}
                                 onChange={(e) => updateHeadcount(role.id, 17, Number(e.target.value))}
                                className="w-full h-8 text-sm text-center bg-transparent border-none outline-none rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
                                min="0"
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Operating Costs Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOperatingSectionCollapsed(!operatingSectionCollapsed)}
                    className="p-1 h-6 w-6"
                  >
                    {operatingSectionCollapsed ? (
                      <ChevronRight className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                  <CardTitle className="text-lg">
                    <DollarSign className="w-5 h-5 text-muted-foreground inline mr-2" />
                    Operating Costs
                  </CardTitle>
                </div>
                {operatingSectionCollapsed && (
                  <div className="text-right">
                     <div className="text-sm text-muted-foreground">Total 22-Month Cost</div>
                     <div className="text-lg font-semibold">₹{(calculateTotalOperatingCosts / 100000).toFixed(0)}L</div>
                  </div>
                )}
              </div>
              {!operatingSectionCollapsed && (
                <CardDescription className="text-sm text-muted-foreground ml-8">
                  Track your recurring operational expenses and infrastructure costs
                </CardDescription>
              )}
            </CardHeader>
            {!operatingSectionCollapsed && (
              <CardContent className="p-0">
                <div className="overflow-x-auto border border-border rounded-lg">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow className="border-b-2 border-border bg-muted/30 hover:bg-muted/30">
                        <TableHead className="text-left py-3 px-4 text-sm font-semibold text-foreground border-r border-border min-w-[250px]">Category</TableHead>
                        <TableHead className="text-left py-3 px-4 text-sm font-semibold text-foreground border-r border-border min-w-[120px]">Type</TableHead>
                        {displayMonthIndices.map((i) => (
                          <TableHead key={i} className="text-center py-3 px-2 text-sm font-semibold text-foreground border-r border-border last:border-r-0 min-w-[70px]">
                            M{i + 1}
                          </TableHead>
                        ))}
                         {costData.projectionMonths > 12 && (
                           <TableHead className="text-center py-3 px-2 text-sm font-semibold text-foreground min-w-[70px]">
                             M22
                           </TableHead>
                         )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costData.operatingCosts.map((opCost, index) => (
                        <TableRow key={opCost.id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                          <TableCell className="py-2 px-4 border-r border-border/30">
                            <input
                              value={opCost.name}
                              onChange={(e) => setCostData(prev => ({
                                ...prev,
                                operatingCosts: prev.operatingCosts.map(cost =>
                                  cost.id === opCost.id ? { ...cost, name: e.target.value } : cost
                                )
                              }))}
                              className="bg-transparent border-none outline-none font-medium text-sm w-full h-8 px-2 rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
                              placeholder="Cost category"
                            />
                          </TableCell>
                          <TableCell className="py-2 px-4 border-r border-border/30">
                            <Badge variant="outline" className="capitalize text-xs h-6">
                              {opCost.category}
                            </Badge>
                          </TableCell>
                          {opCost.monthlyCosts.slice(0, 12).map((cost, monthIndex) => (
                            <TableCell key={monthIndex} className={`py-2 px-2 text-center border-r border-border/30 last:border-r-0 ${getCostColor(cost, getMaxOperatingCost())}`}>
                              <div className="relative">
                                <span className="absolute left-1 top-1/2 -translate-y-1/2 text-muted-foreground text-xs z-10">₹</span>
                                <input
                                  type="number"
                                  value={cost}
                                  onChange={(e) => updateOperatingCost(opCost.id, monthIndex, Number(e.target.value))}
                                  className="w-full h-8 pl-4 pr-1 text-sm text-right bg-transparent border-none outline-none rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
                                  min="0"
                                />
                              </div>
                            </TableCell>
                          ))}
                          {costData.projectionMonths > 12 && (
                            <TableCell className={`py-2 px-2 text-center ${getCostColor(opCost.monthlyCosts[17], getMaxOperatingCost())}`}>
                              <div className="relative">
                                <span className="absolute left-1 top-1/2 -translate-y-1/2 text-muted-foreground text-xs z-10">₹</span>
                                <input
                                  type="number"
                                  value={opCost.monthlyCosts[17]}
                                  onChange={(e) => updateOperatingCost(opCost.id, 17, Number(e.target.value))}
                                  className="w-full h-8 pl-4 pr-1 text-sm text-right bg-transparent border-none outline-none rounded focus:bg-background focus:border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
                                  min="0"
                                />
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Summary Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-muted-foreground" />
                Monthly Cost Summary
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Complete breakdown of your monthly burn rate and cost structure
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Month</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Headcount</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Salary Costs</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Operating Costs</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Monthly Burn</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Cumulative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: costData.projectionMonths }, (_, monthIndex) => {
                      const headcount = calculateTotalHeadcount(monthIndex);
                      const salaryCost = calculateMonthlySalaryCost(monthIndex);
                      const operatingCost = calculateMonthlyOperatingCost(monthIndex);
                      const totalCost = calculateTotalMonthlyCost(monthIndex);
                      const cumulativeCost = Array.from({ length: monthIndex + 1 }, (_, i) => calculateTotalMonthlyCost(i)).reduce((a, b) => a + b, 0);

                      return (
                        <tr key={monthIndex} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                          <td className="py-4 px-6 text-sm font-medium">{monthIndex + 1}</td>
                          <td className="py-4 px-6 text-sm">{headcount}</td>
                          <td className="py-4 px-6 text-sm font-mono">₹{salaryCost.toLocaleString()}</td>
                          <td className="py-4 px-6 text-sm font-mono">₹{operatingCost.toLocaleString()}</td>
                          <td className="py-4 px-6 text-sm font-mono font-semibold text-destructive">₹{totalCost.toLocaleString()}</td>
                          <td className="py-4 px-6 text-sm font-mono">₹{cumulativeCost.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}